import Joi from 'joi'
import clsx from 'clsx'
import { joiResolver } from '@hookform/resolvers/joi'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { types } from '../../../../shared/eventEmitter'
import BaseModal from '../../../../UI/BaseModal/BaseModal'
import Btn from '../../../../UI/Btn'
import TextFieldNumber from '../../../../UI/TextFieldNumber'
import TabBar from '../../../../UI/TabBar'
import RecordComments from '../../../../UI/RecordComments'
import styles from '../style.module.scss'
import http from '../../../../shared/http'
import { TApi, RECORD_TYPES } from '../../../../shared/const'
import { toast } from 'react-toastify'
import dangerIcon from '../../../../assets/icons/Danger Triangle.svg'
import { customNumberFormat } from '../../../../shared/helpers'

// Die Meldungen des Prüfschemas landen unverändert unter dem Feld — deshalb
// kommen sie übersetzt herein und nicht als englischer Rohtext.
const buildSchema = t =>
	Joi.object({
		paid: Joi.number()
			.custom((value, helper) => {
				const { amount } = helper.prefs.data
				if (Math.round(amount * 100) / 100 >= value) return value
				return helper.message(t('No more than the invoice amount'))
			})
			.required()
			.messages({
				'number.base': t('Please enter an amount'),
				'any.required': t('Please enter an amount')
			})
	})

// Beträge stehen im Fenster wie in der Liste: deutsches Zahlenformat mit zwei
// Nachkommastellen. `customNumberFormat` gibt für 0 die Zahl 0 zurück, hier
// soll aber „0,00" stehen.
const money = value => `€ ${customNumberFormat(Math.round((value || 0) * 100) / 100) || '0,00'}`

// Farbe der Statuskennzeichnung — dieselbe Bedeutung wie in der Rechnungsliste.
const STATUS_TONES = {
	Paid: 'invoiceStatusPaid',
	Credit: 'invoiceStatusCredit',
	Overdue: 'invoiceStatusOverdue',
	Unpaid: 'invoiceStatusUnpaid'
}

export default function InvoiceChangeData() {
	const { t } = useTranslation()
	const [data, setData] = useState({})
	const {
		handleSubmit,
		reset,
		trigger,
		control,
		formState: { errors, dirtyFields }
	} = useForm({
		resolver: joiResolver(buildSchema(t), {
			data
		})
	})

	const [id, setId] = useState(null)
	const [isLoading, setLoading] = useState(false)
	const [onDone, setOnDone] = useState(null)
	const [tab, setTab] = useState('payment')

	useEffect(() => {
		if (Object.keys(errors).length) trigger()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localStorage.getItem('i18nextLng')])

	const handleOpen = (invoice, onDone = null) => {
		setOnDone(() => onDone)

		if (!invoice) return
		setTab('payment')
		setId(invoice.id)
		setData(invoice)
		reset({ paid: invoice.amount - invoice.balance })
	}

	const onSubmit = (form, closeModal) => {
		setLoading(true)
		http.put(TApi.ADMIN_PUT_INVOICES(id), form)
			.then(() => {
				toast.success(t('Status was updated'))
				setId(null)
				closeModal()
			})
			.then(() => {
				if (onDone) onDone()
			})
			.finally(() => setLoading(false))
	}

	const handleClose = () => {
		reset({})
		setId(null)
		setData({})
	}

	// Was bereits eingegangen ist, steht nicht am Datensatz: Der Saldo ist der
	// noch offene Rest des Rechnungsbetrags.
	const alreadyPaid = (data.amount || 0) - (data.balance || 0)

	return (
		<BaseModal
			onClose={handleClose}
			onOpen={handleOpen}
			nameEvent={types.openInvoiceChangeData}
			isDirty={Boolean(Object.keys(dirtyFields).length)}
			title={t('Change payment')}
			isLoading={isLoading}
			maxWidth={500}
		>
			{({ closeModal }) => (
				<>
					<TabBar
						className={styles.recordTabs}
						tabs={[
							{ key: 'payment', label: 'Payment' },
							{ key: 'comments', label: 'Comments' }
						]}
						active={tab}
						onChange={setTab}
					/>

					{tab === 'comments' && <RecordComments entityType={RECORD_TYPES.INVOICE} entityId={id} />}

					<form
						className={clsx(styles.container, styles.invoiceForm, tab !== 'payment' && styles.recordHidden)}
						onSubmit={handleSubmit(form => onSubmit(form, closeModal))}
					>
						{/*
						 * Zu welcher Rechnung wird hier gebucht? Ohne Nummer und Betrag
						 * war das Fenster ein Eingabefeld ohne Zusammenhang — die Zahlen
						 * standen nur in der Zeile dahinter, die der Dialog verdeckt.
						 */}
						<div className={styles.invoiceCard}>
							<div className={styles.invoiceCardHead}>
								<span className={styles.invoiceNo}>
									{t('Invoice')} {data.no || '—'}
								</span>
								{data.status && (
									<span className={clsx(styles.invoiceStatus, styles[STATUS_TONES[data.status]])}>
										{t(data.status)}
									</span>
								)}
							</div>
							{Boolean(data.company || data.school || data.createdAt) && (
								<p className={styles.invoiceMeta}>
									{[data.company, data.school, data.createdAt].filter(Boolean).join(' · ')}
								</p>
							)}
							<dl className={styles.invoiceFigures}>
								<div className={styles.invoiceFigure}>
									<dt>{t('Invoice amount')}</dt>
									<dd className={styles.invoiceFigureStrong}>{money(data.amount)}</dd>
								</div>
								<div className={styles.invoiceFigure}>
									<dt>{t('Already paid')}</dt>
									<dd>{money(alreadyPaid)}</dd>
								</div>
								<div className={styles.invoiceFigure}>
									<dt>{t('Open balance')}</dt>
									{/* Rot nur, wenn wirklich etwas aussteht — ein negativer
									    Saldo ist ein Guthaben, keine offene Forderung. */}
									<dd className={clsx(data.balance > 0 && styles.invoiceFigureOpen)}>
										{money(data.balance)}
									</dd>
								</div>
							</dl>
						</div>

						<div className={styles.attentionCard}>
							<img className={styles.warningImage} src={dangerIcon} alt="" />
							<div>
								<h3 className={styles.cardTitle}>{t('Important')}</h3>
								<p className={styles.cardDescription}>
									{t(
										'If the invoice amount matches the payment, the invoice status changes to paid. If there is a difference, the balance is not equal to zero.'
									)}
								</p>
							</div>
						</div>
						<section className={styles.sectionCenter}>
							<TextFieldNumber
								disabled={isLoading}
								// Die Fehlermeldung haengt unter dem Feld — mit weniger
								// Abstand laege sie auf dem Knopf.
								mb={30}
								label={t('Amount received in €')}
								name="paid"
								control={control}
								placeholder="0,00"
								error={errors.paid}
							/>
							<Btn
								loading={isLoading}
								width={337}
								type="submit"
								icon="arrow"
								iconSize={{ w: 10, h: 10 }}
								iconColor="#fff"
							>
								{t('save')}
							</Btn>
						</section>
					</form>
				</>
			)}
		</BaseModal>
	)
}
